import { useSyncExternalStore } from "react";

// Unsent text per chat, kept in this browser so switching chats (or reloading) never loses what you were typing.
// Keys are the chat list keys, "dm:<id>" and "group:<id>". The Chat list shows a "Draft" label for any chat with one.

const PREFIX = "fg.connect.draft:";
const listeners = new Set<() => void>();
let cache: Record<string, string> | null = null;

function load(): Record<string, string> {
    if (cache) return cache;
    const out: Record<string, string> = {};
    try {
        for (let i = 0; i < window.localStorage.length; i++) {
            const k = window.localStorage.key(i);
            if (k?.startsWith(PREFIX)) {
                const v = window.localStorage.getItem(k);
                if (v) out[k.slice(PREFIX.length)] = v;
            }
        }
    } catch {
        /* private mode: drafts last for this session only */
    }
    cache = out;
    return out;
}

export function readDraft(key: string): string {
    return load()[key] ?? "";
}

/** Saves `text` as the draft of `key`; empty text removes it. */
export function writeDraft(key: string, text: string) {
    const cur = load();
    const had = key in cur;
    if (!text.trim()) {
        if (!had) return;
        const { [key]: _gone, ...rest } = cur;
        cache = rest;
        try { window.localStorage.removeItem(PREFIX + key); } catch { /* ignore */ }
    } else {
        if (cur[key] === text) return;
        cache = { ...cur, [key]: text };
        try { window.localStorage.setItem(PREFIX + key, text); } catch { /* ignore */ }
    }
    listeners.forEach((l) => l());
}

/** The drafts of every chat; re-renders when one changes (also from another tab). */
export function useDrafts(): Record<string, string> {
    return useSyncExternalStore(
        (cb) => {
            listeners.add(cb);
            const onStorage = (e: StorageEvent) => {
                if (e.key?.startsWith(PREFIX)) { cache = null; cb(); }
            };
            window.addEventListener("storage", onStorage);
            return () => { listeners.delete(cb); window.removeEventListener("storage", onStorage); };
        },
        load
    );
}
